import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Sparkles, Send, X } from "lucide-react";

interface ContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  buttonText?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
}

export function ContactDialog({
  isOpen,
  onClose,
  title = "Get Started with Kartly",
  description = "Transform your business with AI-powered eCommerce solutions. Let's discuss how we can help you scale.",
  buttonText = "Get Started",
}: ContactDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.company) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Success feedback
      toast.success("Thank you! We'll be in touch within 24 hours.");

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
      });

      // Close dialog
      onClose();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="contact-form-dialog max-w-md mx-auto p-0 border-0 bg-transparent shadow-none overflow-hidden">
        {/* Enhanced Glass Effect Container with Dark Mode Support */}
        <div className="relative rounded-2xl p-8 overflow-hidden backdrop-blur-xl bg-white/85 dark:bg-gray-900/85 border border-white/30 dark:border-gray-700/30 shadow-2xl">
          {/* Adaptive Background gradient effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-50/70 to-orange-50/70 dark:from-red-950/40 dark:to-orange-950/40 rounded-2xl"></div>

          {/* Floating gradient orbs - adaptive colors */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-red-100/50 to-transparent dark:from-red-800/30 dark:to-transparent rounded-full blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-orange-100/50 to-transparent dark:from-orange-800/30 dark:to-transparent rounded-full blur-2xl"></div>

          {/* Additional glass effect enhancement */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/15 to-transparent dark:from-white/8 dark:to-transparent rounded-2xl"></div>

          {/* Close button */}
          {/* <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X size={20} />
          </button> */}

          <div className="relative z-10">
            <DialogHeader className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-full">
                  <Sparkles className="text-white" size={24} />
                </div>
              </div>
              <DialogTitle className="text-2xl font-bold text-primary-light mb-2">
                {title}
              </DialogTitle>
              <DialogDescription className="text-secondary-light leading-relaxed">
                {description}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="text-primary-light font-medium"
                >
                  Full Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="form-field-glow bg-white/90 dark:bg-gray-800/90 border-red-200 dark:border-red-700 focus:border-red-400 dark:focus:border-red-500 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-all duration-300"
                  required
                />
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-primary-light font-medium"
                >
                  Business Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@company.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="form-field-glow bg-white/90 dark:bg-gray-800/90 border-red-200 dark:border-red-700 focus:border-red-400 dark:focus:border-red-500 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-all duration-300"
                  required
                />
              </div>

              {/* Phone Field */}
              <div className="space-y-2">
                <Label
                  htmlFor="phone"
                  className="text-primary-light font-medium"
                >
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="form-field-glow bg-white/90 dark:bg-gray-800/90 border-red-200 dark:border-red-700 focus:border-red-400 dark:focus:border-red-500 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-all duration-300"
                />
              </div>

              {/* Company Field */}
              <div className="space-y-2">
                <Label
                  htmlFor="company"
                  className="text-primary-light font-medium"
                >
                  Company Name *
                </Label>
                <Input
                  id="company"
                  type="text"
                  placeholder="Your company name"
                  value={formData.company}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  className="form-field-glow bg-white/90 dark:bg-gray-800/90 border-red-200 dark:border-red-700 focus:border-red-400 dark:focus:border-red-500 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-all duration-300"
                  required
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 py-3 text-lg font-medium transition-all duration-700 hover:scale-105 shadow-xl hover:shadow-red-500/25 btn-interactive"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Send size={18} className="mr-2" />
                      {buttonText}
                    </div>
                  )}
                </Button>
              </div>

              {/* Privacy Note */}
              <div className="text-center pt-2">
                <p className="text-xs text-muted-light">
                  We respect your privacy. Your information will be used to
                  contact you about our services.
                </p>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
